import { Component, Inject, OnDestroy, ViewEncapsulation } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { AuthenticationService } from '@core/authentication/authentication.service';
import { MessageService } from '@core/errors/messages-service';
import { ExecuteActionQuotationInput, Quotation } from '@core/interfaces/quotations';
import { getErrorCode } from '@core/utils/get-error-code';
import { environment } from '@environment';
import { ConfirmDialogComponent } from '@fuse/components/confirm-dialog/confirm-dialog.component';
import { FuseProgressBarService } from '@fuse/components/progress-bar/progress-bar.service';
import { FuseTranslationLoaderService } from '@fuse/services/translation-loader.service';
import { TranslateService } from '@ngx-translate/core';
import { ConfirmDialogService } from '../../../../@fuse/components/confirm-dialog/confirm-dialog.service';
import english from '../i18n/en.json';
import spanish from '../i18n/es.json';
import portuguese from '../i18n/pt.json';
import { ReloadResult } from '../quotations.component';
import { QuotationService } from '../services/quotation.service';
import { DialogUpdatedComponent } from './dialog-updated/dialog-updated.component';

export interface QuotationDialogData {
  quotation: Quotation;
  isViewMode?: boolean;
  hasFeatureApproveQuotationByPanel?: boolean;
}

@Component({
  templateUrl: './dialog-quotation.component.html',
  styleUrls: ['./dialog-quotation.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class DialogQuotationComponent implements OnDestroy {
  public quotation: Quotation;
  public reportUrl = `${environment.GROUND_BASE_URL}/#/orcamentos/`;
  public isViewMode: boolean;
  public hasFeatureApproveQuotationByPanel: boolean;
  public refreshDatatable = false;

  public hasLabelViewPermission = this.authenticationService.hasPermission('labels-list');


  constructor(
    @Inject(MAT_DIALOG_DATA) readonly data: QuotationDialogData,
    public readonly matDialogRef: MatDialogRef<DialogQuotationComponent, ReloadResult>,
    private fuseTranslationLoaderService: FuseTranslationLoaderService,
    public readonly fuseProgressBarService: FuseProgressBarService,
    private readonly translateService: TranslateService,
    private readonly quotationService: QuotationService,
    private readonly messageService: MessageService,
    private authenticationService: AuthenticationService,
    private readonly matDialog: MatDialog,
    private readonly confirmDialogService: ConfirmDialogService
  ) {
    this.quotation = data.quotation;
    this.isViewMode = data.isViewMode;
    this.hasFeatureApproveQuotationByPanel = data.hasFeatureApproveQuotationByPanel;
    this.fuseTranslationLoaderService.loadJsonTranslations({ english, portuguese, spanish });
  }

  ngOnDestroy(): void {
    this.matDialogRef.close({ reload: this.refreshDatatable });
  }

  async executeAction(action: 'approve' | 'refuse'): Promise<void> {
    const actionUpperCased = action.toUpperCase();

    const result = await this.confirmDialogService.open({
      title: this.translateService.instant(`QUOTATIONS.CONFIRM.TITLE_${actionUpperCased}`),
      message: this.translateService.instant(`QUOTATIONS.CONFIRM.MESSAGE_${actionUpperCased}`),
      okText: this.translateService.instant(`QUOTATIONS.CONFIRM.CONTINUE_AND_${actionUpperCased}`),
      cancelText: this.translateService.instant('QUOTATIONS.CONFIRM.CANCEL_TEXT'),
      inputLabel: this.translateService.instant('QUOTATIONS.CONFIRM.MESSAGE_PLACEHOLDER')
    });

    if (!result || !result.confirmed) {
      return;
    }

    try {
      this.fuseProgressBarService.show();

      const input: ExecuteActionQuotationInput = {
        id: this.quotation.id,
        updatedAt: this.quotation.updatedAt,
        approvedMessage: result.message
      };

      if (action === 'approve') {
        await this.quotationService.approveQuotation(input);
      } else {
        await this.quotationService.rejectQuotation(input);
      }

      this.messageService.success(`QUOTATIONS.MESSAGES.SUCCESS_${actionUpperCased}`, {
        identifier: this.quotation.identifier
      });

      this.matDialogRef.close({ reload: true });
    } catch (err) {
      const code = getErrorCode(err);
      if (code === 'quotation_is_outdated') {
        await this.updateOutdatedQuotation();
        return;
      }

      this.messageService.error(err, 'QUOTATIONS.ERRORS.UNKNOWN_ERROR');
    } finally {
      this.fuseProgressBarService.hide();
    }
  }

  private async updateOutdatedQuotation(): Promise<void> {
    await this.matDialog.open(DialogUpdatedComponent, {
      maxWidth: '380px'
    }).afterClosed().toPromise();

    this.fuseProgressBarService.show();
    this.quotation = await this.quotationService.getById(this.quotation.id);
    this.fuseProgressBarService.hide();
  }

  public wasUpdatedLabels(): void {
    this.refreshDatatable = true;
  }
}
